import { Timestamp, WeekDays } from '@griply/types';
import {
  addDays,
  addMonths,
  addWeeks,
  differenceInDays,
  differenceInWeeks,
  endOfWeek,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

import { weekdaysNumberMap } from './constants';

export enum TimeProgressTickRange {
  Day = 'day',
  Week = 'week',
  Month = 'month',
  Months = 'months',
  MonthsWithYear = 'monthsWithYear',
}

export type TimeProgressChartTicksOptions = {
  entries: { date: Timestamp; value: number }[];
  startDate?: Timestamp | null;
  endDate?: Timestamp | null;
  chartStartDate?: Timestamp | null;
  chartEndDate?: Timestamp | null;
  today: Timestamp;
  completed?: boolean;
  weekStartsOn: WeekDays;
  availableWidth?: number;
};

export type TimeProgressChartTicks = {
  domain: [number, number];
  ticks: number[];
  tickRange: TimeProgressTickRange;
  yearBoundaries: number[];
};

const twoDays = 2 * 24 * 60 * 60 * 1000;

const ADAPTIVE_STEPS_IN_MONTHS = [
  1, 2, 3, 4, 6, 12, 24, 60, 120, 300, 600, 1200,
];
const LABEL_WIDTH_WITH_YEAR = 88;
const LABEL_WIDTH_MONTH_ONLY = 24;
const MIN_ADAPTIVE_TICKS = 2;
const MAX_ADAPTIVE_TICKS = 12;
const DEFAULT_AVAILABLE_WIDTH = 320;

const buildAlignedMonthTicks = (
  domain: [number, number],
  stepInMonths: number,
): number[] => {
  const start = new Date(domain[0]);
  const startGlobalMonth = start.getFullYear() * 12 + start.getMonth();
  const remainder = startGlobalMonth % stepInMonths;
  const firstAlignedMonth =
    remainder === 0
      ? startGlobalMonth
      : startGlobalMonth + (stepInMonths - remainder);

  const ticks: number[] = [];
  const collect = (globalMonth: number): void => {
    const time = new Date(
      Math.floor(globalMonth / 12),
      globalMonth % 12,
      1,
    ).getTime();
    if (time >= domain[1]) {
      return;
    }
    if (time >= domain[0]) {
      ticks.push(time);
    }
    collect(globalMonth + stepInMonths);
  };
  collect(firstAlignedMonth);

  return ticks;
};

const buildYearBoundaries = (domain: [number, number]): number[] => {
  const startYear = new Date(domain[0]).getFullYear();
  const endYear = new Date(domain[1]).getFullYear();

  return Array.from({ length: endYear - startYear + 1 }, (_, index) =>
    new Date(startYear + index, 0, 1).getTime(),
  ).filter((time) => time > domain[0] && time < domain[1]);
};

const selectAdaptiveMonthTicks = (
  domain: [number, number],
  maxTicks: number,
  stepIndex: number,
): number[] => {
  const ticks = buildAlignedMonthTicks(
    domain,
    ADAPTIVE_STEPS_IN_MONTHS[stepIndex],
  );
  const isLastStep = stepIndex === ADAPTIVE_STEPS_IN_MONTHS.length - 1;
  if (ticks.length <= maxTicks || isLastStep) {
    return ticks;
  }

  return selectAdaptiveMonthTicks(domain, maxTicks, stepIndex + 1);
};

const getTickRange = (domain: [number, number]): TimeProgressTickRange => {
  if (
    isSameMonth(domain[0], domain[1]) &&
    isFirstDayOfMonth(domain[0]) &&
    isLastDayOfMonth(domain[1])
  ) {
    return TimeProgressTickRange.Month;
  }

  const difInWeeks = differenceInWeeks(domain[1], domain[0]);

  if (difInWeeks <= 1) {
    return TimeProgressTickRange.Day;
  }

  if (difInWeeks < 6) {
    return TimeProgressTickRange.Week;
  }

  return TimeProgressTickRange.Months;
};

export const getTimeProgressChartTicks = ({
  entries,
  startDate,
  endDate,
  chartStartDate,
  chartEndDate,
  today,
  completed,
  weekStartsOn,
  availableWidth,
}: TimeProgressChartTicksOptions): TimeProgressChartTicks => {
  const effectiveStartDate = chartStartDate ?? startDate;
  const effectiveEndDate = chartEndDate ?? endDate;

  const entryDates = entries.map(({ date }) => date.getTime());
  const minDate = Math.min(
    ...entryDates,
    ...(effectiveStartDate ? [effectiveStartDate.getTime()] : []),
    ...(!completed ? [today.getTime()] : []),
  );
  const maxDate = Math.max(
    ...entryDates,
    ...(effectiveEndDate ? [effectiveEndDate.getTime()] : []),
    ...(!completed ? [today.getTime()] : []),
  );

  const strictDates = !!(chartStartDate && chartEndDate);
  let domain: [number, number];

  if (!strictDates && (differenceInDays(maxDate, minDate) > 2 || completed)) {
    domain = [minDate, maxDate];
  } else {
    domain = [
      (effectiveStartDate ?? addDays(today, -1)).getTime(),
      (effectiveEndDate ?? addDays(today, 1)).getTime(),
    ];
  }

  const tickRange = getTickRange(domain);
  const yearBoundaries = buildYearBoundaries(domain);

  if (tickRange === TimeProgressTickRange.Months) {
    const currentYear = today.getFullYear();
    const showYear =
      new Date(domain[0]).getFullYear() !== currentYear ||
      new Date(domain[1]).getFullYear() !== currentYear;
    const labelWidth = showYear
      ? LABEL_WIDTH_WITH_YEAR
      : LABEL_WIDTH_MONTH_ONLY;
    const width = availableWidth ?? DEFAULT_AVAILABLE_WIDTH;
    const maxTicks = Math.max(
      MIN_ADAPTIVE_TICKS,
      Math.min(MAX_ADAPTIVE_TICKS, Math.floor(width / labelWidth)),
    );

    return {
      domain,
      ticks: selectAdaptiveMonthTicks(domain, maxTicks, 0),
      tickRange: showYear
        ? TimeProgressTickRange.MonthsWithYear
        : TimeProgressTickRange.Months,
      yearBoundaries,
    };
  }

  const range = domain[1] - domain[0];
  let rangeDates: number[] = [];
  let currentDate = startOfDay(domain[0]);
  rangeDates.push(currentDate.getTime());

  if (tickRange === TimeProgressTickRange.Month || range < twoDays) {
    currentDate = new Date(domain[1]);
    rangeDates.push(domain[1]);
  }

  const dateFnsWeekOptions = { weekStartsOn: weekdaysNumberMap[weekStartsOn] };

  const advanceFn: Record<TimeProgressTickRange, (date: Date) => Date> = {
    [TimeProgressTickRange.Day]: (date) => addDays(date, 1),
    [TimeProgressTickRange.Week]: (date) =>
      addWeeks(startOfWeek(date, dateFnsWeekOptions), 1),
    [TimeProgressTickRange.Month]: (date) => addMonths(startOfMonth(date), 1),
    [TimeProgressTickRange.Months]: (date) => addMonths(startOfMonth(date), 1),
    [TimeProgressTickRange.MonthsWithYear]: (date) =>
      addMonths(startOfMonth(date), 1),
  };

  const advance = advanceFn[tickRange];
  const collectTicks = (date: Date): void => {
    const next = advance(date);
    if (next.getTime() >= domain[1]) {
      return;
    }
    rangeDates.push(next.getTime());
    collectTicks(next);
  };
  collectTicks(currentDate);

  if (tickRange === TimeProgressTickRange.Week) {
    rangeDates = rangeDates.map((date) =>
      endOfWeek(date, dateFnsWeekOptions).getTime(),
    );
  }

  const ticks = rangeDates.sort((a, b) => a - b);

  return { domain, ticks, tickRange, yearBoundaries };
};
